import { cloneDeep } from 'lodash';
import { PromptFormats, formatString } from 'wb-reddit/app/common/components/autoNaming/formattingHelper';
import { divideCurrencyFields } from 'wb-reddit/app/routes/superGrid/common/constants';
import { DATE_DISPLAY_FORMAT } from '@aleph/shared-components';
import dayjs from 'dayjs';
import { Column, IRowNode } from 'ag-grid-community';
import useLoadingRowsStore from 'wb-reddit/app/common/components/autoNaming/loadingStore';
import { ApolloClient } from '@apollo/client';
import { MatchColumnNamesDocument } from 'wb-reddit/graphql/generated';
import { RedditLevel, getFundingInstrumentOptions } from 'wb-reddit/app/routes/superGrid/utils';
import { getLevelDepth, useSupergridMutations, getLevelByDepth } from '@aleph/supergrid';
import { REDDIT_SUPERGRID_CONFIG } from 'wb-reddit/app/routes/superGrid/config';
import { RedditCreateLevels, RedditSuperGridMutationsConfig } from 'wb-reddit/app/routes/superGrid/mutator/utils';

const MAX_GRID_DEPTH = 3;

const ALL_COLUMNS = [
	'objective',
	'conversionGoal',
	'bidStrategy',
	'bidAmount',
	'budgetType',
	'budget',
	'start',
	'end',
	'gender',
	'communities',
	'communitiesExcluded',
	'interests',
	'keywords',
	'customAudiences',
	'customAudiencesExcluded',
	'locations',
	'locationsExcluded',
	'appUrlId',
	'deviceType',
	'devices',
	'minOSVersion',
	'cellCarriers',
	'postType',
	'headline',
	'content',
	'profile',
	'imageVideo',
	'caption',
	'destinationUrl',
	'displayUrl',
	'callToAction',
	'thumbnailImage',
	'allowComments',
	'clickEventTrackers',
	'spendCap',
	'fundingInstrument',
] as const;

const STRING_FORMATS = [
	'LOWERCASE',
	'UPPERCASE',
	'CAMEL_CASE',
	'PASCAL_CASE',
	'KEBAB_CASE_CAPITALIZED',
	'TITLE_CASE',
];

type ColumnNames = typeof ALL_COLUMNS[number];

type ColumnNameResolver = { [K in ColumnNames]: {
	level: RedditLevel,
	resolver: (data: any) => string
} };

const COLUMN_DATA_RESOLVERS: ColumnNameResolver = {
	allowComments: {
		level: RedditLevel.AD,
		resolver(data) {
			return data?.creativeAllowComments === undefined ? ''
				: data.creativeAllowComments ? 'COMMENTS' : 'NO_COMMENTS';
		},
	},
	appUrlId: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.groupAppId ?? '';
		},
	},
	bidAmount: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return String(data?.groupBidAmount ? ((data?.groupBidAmount ?? 0) / divideCurrencyFields.spend) : '');
		},
	},
	bidStrategy: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.groupBidStrategy ?? '';
		},
	},
	budget: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return String(data?.groupGoalAmount ? ((data?.groupGoalAmount ?? 0) / divideCurrencyFields.spend) : '');
		},
	},
	budgetType: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.groupGoal ?? '';
		},
	},
	callToAction: {
		level: RedditLevel.AD,
		resolver(data) {
			return data?.creativeContentCallToAction0 ?? '';
		},
	},
	caption: {
		level: RedditLevel.AD,
		resolver(data) {
			return data?.creativeContentCaption0 ?? '';
		},
	},
	cellCarriers: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.carriers?.[0]?.value ?? '';
		},
	},
	clickEventTrackers: {
		level: RedditLevel.AD,
		resolver(data) {
			return data?.adClickEventTrackers?.[0]?.url ?? '';
		},
	},
	communities: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.communities?.[0]?.label ?? '';
		},
	},
	communitiesExcluded: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.communitiesExcluded?.[0]?.label ?? '';
		},
	},
	content: {
		level: RedditLevel.AD,
		resolver(data) {
			return data?.creativeText ?? '';
		},
	},
	conversionGoal: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.groupOptimizationGoal ?? '';
		},
	},
	customAudiences: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.customAudiences?.[0]?.label ?? '';
		},
	},
	customAudiencesExcluded: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.customAudiencesExcluded?.[0]?.label ?? '';
		},
	},
	destinationUrl: {
		level: RedditLevel.AD,
		resolver(data) {
			return data?.creativeContentDestinationUrl0 ?? '';
		},
	},
	deviceType: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.deviceType?.[0]?.label ?? '';
		},
	},
	devices: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.devices?.[0]?.label ?? '';
		},
	},
	displayUrl: {
		level: RedditLevel.AD,
		resolver(data) {
			return data?.creativeContentDisplayUrl0 ?? '';
		},
	},
	end: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.groupEndTime ? dayjs(data.groupEndTime).format(DATE_DISPLAY_FORMAT) : '';
		},
	},
	fundingInstrument: {
		level: RedditLevel.CAMPAIGN,
		resolver(data) {
			const options = getFundingInstrumentOptions(data);

			return options.find((option) => option.value === data?.fundingInstrument)?.label ?? '';
		},
	},
	start: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.groupStartTime ? dayjs(data.groupStartTime).format(DATE_DISPLAY_FORMAT) : '';
		},
	},
	gender: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.gender ?? '';
		},
	},
	headline: {
		level: RedditLevel.AD,
		resolver(data) {
			return data?.creativeHeadline ?? '';
		},
	},
	imageVideo: {
		level: RedditLevel.AD,
		resolver(data) {
			return data?.creativeContentMediaUrl0?.mediaUrl ?? '';
		},
	},
	interests: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.interests?.[0]?.label ?? '';
		},
	},
	keywords: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.keywords?.[0]?.label ?? '';
		},
	},
	locations: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.locations?.[0]?.country ?? '';
		},
	},
	locationsExcluded: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.locationsExcluded?.[0]?.country ?? '';
		},
	},
	minOSVersion: {
		level: RedditLevel.GROUP,
		resolver(data) {
			return data?.groupTargetingDevicesOSMinVersion ?? '';
		},
	},
	objective: {
		level: RedditLevel.CAMPAIGN,
		resolver(data) {
			return data?.campaignObjective ?? '';
		},
	},
	postType: {
		level: RedditLevel.AD,
		resolver(data) {
			return data?.creativeType ?? '';
		},
	},
	profile: {
		level: RedditLevel.AD,
		resolver(data) {
			return data?.profile?.label ?? '';
		},
	},
	spendCap: {
		level: RedditLevel.CAMPAIGN,
		resolver(data) {
			const spendCap = data?.campaignSpendCap;

			if (!spendCap) {
				return 'Unset';
			}

			if (typeof spendCap === 'number') {
				return String(spendCap / divideCurrencyFields.spend);
			}

			return '';
		},
	},
	thumbnailImage: {
		level: RedditLevel.AD,
		resolver(data) {
			return data?.creativeThumbnailUrl ?? '';
		},
	},
};

async function findTopMatch(
	userInput: string[][],
	client: ApolloClient<any>,
) {
	try {
		const { data } = await client.query({
			query:     MatchColumnNamesDocument,
			variables: {
				columnNames:      ALL_COLUMNS as unknown as string[],
				columnNamesArray: userInput,
				model:            'gpt-4o',
				stringFormats:    STRING_FORMATS,
				temperature:      0,
			},
		});

		const toolCall = data.openAiCompletion?.[0]?.message?.toolCalls?.[0];

		if (toolCall) {
			const functionArgs = JSON.parse(toolCall?.function?.arguments as string);

			return functionArgs.column_name as { columnName: ColumnNames, format: PromptFormats }[][];
		}

		throw Error('Incorrect reponse');
	} catch (error) {
		console.error(`Error during API call: ${error}`);

		return [];
	}
}

function extractVariablesFromNamingExpression(input: string) {
	// Regular expression to match variables preceded by $
	const regex = /\$(?:\(([^()]*?)\)|(\w+))/g;

	return [...input.matchAll(regex)].map(item => item[0].substring(1));
}

const getPropertyFromData = (data: any, columnName: ColumnNames) => {
	const columnDataResolver = COLUMN_DATA_RESOLVERS[columnName];

	if (data?.supergridLevel === columnDataResolver.level) {
		return columnDataResolver.resolver(data);
	}

	const currLevelDepth = getLevelDepth(REDDIT_SUPERGRID_CONFIG, data?.supergridLevel);
	const targetLevelDepth = getLevelDepth(REDDIT_SUPERGRID_CONFIG, columnDataResolver.level);

	if (targetLevelDepth > currLevelDepth) {
		if (currLevelDepth >= MAX_GRID_DEPTH) {
			return '';
		}
		const nextLevel = getLevelByDepth(REDDIT_SUPERGRID_CONFIG, currLevelDepth + 1) as unknown as RedditCreateLevels;
		const mutations = useSupergridMutations.getState<RedditSuperGridMutationsConfig>()
			.getMutationsWithParents(REDDIT_SUPERGRID_CONFIG);
		// @ts-expect-error
		const nextData = mutations[nextLevel].find((mutation) => (
			data.id ? mutation.parent.id === data.id
				: mutation.parent.hash === data.hash
		));

		if (!nextData) {
			return '';
		}

		return getPropertyFromData(nextData, columnName);
	}

	if (!data?.parent) {
		return '';
	}

	return getPropertyFromData(data.parent, columnName);
};

export const transformNamingExpression = async (
	input: { data: any, expression: string }[],
	client: ApolloClient<any>,
) => {
	const variables = input.map(({ expression }) => extractVariablesFromNamingExpression(expression));

	const columnNameMatches = await findTopMatch(variables.map(item => item.map((variable) => {
		if (variable.startsWith('(') && variable.endsWith(')')) {
			return variable.substring(1, variable.length - 1);
		}

		return variable;
	})), client);

	return input.map((namingEntity, i) => {
		const matchingExpressionVariables = variables[i];
		const columnNameMatch = columnNameMatches[i];
		let replacedExpression = cloneDeep(namingEntity.expression);
		matchingExpressionVariables.forEach((variable, j) => {
			replacedExpression = replacedExpression.replace(
				`$${variable}`,
				formatString(String(getPropertyFromData(namingEntity.data, columnNameMatch[j].columnName)), columnNameMatch[j].format),
			);
		});

		return replacedExpression;
	});
};

export function createSequence(start: number, end: number) {
	if (start > end) {
		const temp = Number(end);
		end = start;
		start = temp;
	}
	const arr: number[] = [];

	for (let i = start; i <= end; i++) {
		arr.push(i);
	}

	return arr;
}

export function isNamingFormula(input?: unknown) {
	return typeof input === 'string' && input.includes('$');
}

export function handleCellLoading(columns: Column[], nodes: IRowNode[], loading: boolean) {
	useLoadingRowsStore.getState().setLoadingRows(loading ? {
		columns: columns.map((col) => col.getColDef().colId ?? ''),
		rows:    nodes.map((node) => node.id ?? ''),
	} : { columns: [], rows: [] });
}

export function changeMutationWithAutoName(
	initialData: any,
	newName: string | undefined,
	expression: string,
	isNameColumn: boolean,
	changeMutation: (mutation: any) => void,
) {
	const newData = { ...initialData };

	if (newName) {
		newData.name = newName;

		if (newName !== "Error: Couldn't generate name") {
			newData.namingExpression = expression;
		}
	}

	if (isNameColumn && !isNamingFormula(expression) && newData.namingExpression) {
		newData.namingExpression = undefined;
	}

	changeMutation(newData);
}
